const SEARCH_HISTORY_STORAGE_KEY = 'luxoRecentSearches';
const SEARCH_HISTORY_LIMIT = 5;

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.searchSuggestions = this.querySelector('#search-suggestions');
    this.searchHistory = this.querySelector('[data-search-history]');
    this.searchHistoryList = this.querySelector('[data-search-history-list]');
    this.searchUrl = this.searchHistoryList?.dataset.searchUrl || this.input?.form?.getAttribute('action') || '/search';

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      this.input.addEventListener('blur', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.hideEmptySearchSuggestions();
        }, 200);
      });
      this.input.addEventListener('focus', () => this.showEmptySearchSuggestions());
      this.addEventListener('click', this.onSearchItemClick.bind(this));
      this.renderRecentSearches();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onFormSubmit() {
    this.saveSearchTerm(this.getQuery());
  }

  onSearchItemClick(event) {
    const clearButton = event.target.closest('[data-search-history-clear]');
    if (clearButton) {
      event.preventDefault();
      this.clearRecentSearches();
      this.input.focus();
      this.showEmptySearchSuggestions();
      return;
    }

    const searchItem = event.target.closest('a.predictive-search__item, button.predictive-search__item');
    if (!searchItem || !this.contains(searchItem)) return;

    const searchTerm = this.getSearchTermFromItem(searchItem);
    this.saveSearchTerm(searchTerm);
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
      this.hideEmptySearchSuggestions();
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
      this.showEmptySearchSuggestions();
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }

  showEmptySearchSuggestions() {
    if (!this.searchSuggestions || this.input.value.length > 0) return;
    this.renderRecentSearches();
    this.searchSuggestions.classList.remove('hidden');
  }

  hideEmptySearchSuggestions() {
    this.searchSuggestions?.classList.add('hidden');
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(localStorage.getItem(SEARCH_HISTORY_STORAGE_KEY));
      if (!Array.isArray(searches)) return [];

      return searches
        .filter((search) => typeof search === 'string' && search.trim().length > 0)
        .slice(0, SEARCH_HISTORY_LIMIT);
    } catch (error) {
      return [];
    }
  }

  saveRecentSearches(searches) {
    try {
      localStorage.setItem(SEARCH_HISTORY_STORAGE_KEY, JSON.stringify(searches));
    } catch (error) {
      // Storage can be unavailable in private browsing modes.
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(SEARCH_HISTORY_STORAGE_KEY);
    } catch (error) {
      this.saveRecentSearches([]);
    }

    this.renderRecentSearches();
    this.markRecentPredictiveSuggestions();
  }

  saveSearchTerm(searchTerm) {
    const normalizedSearchTerm = this.normalizeSearchTerm(searchTerm);
    if (!normalizedSearchTerm) return;

    const recentSearches = this.getRecentSearches();
    const dedupedSearches = recentSearches.filter(
      (recentSearch) => recentSearch.toLowerCase() !== normalizedSearchTerm.toLowerCase()
    );

    this.saveRecentSearches([normalizedSearchTerm, ...dedupedSearches].slice(0, SEARCH_HISTORY_LIMIT));
    this.renderRecentSearches();
    this.markRecentPredictiveSuggestions();
  }

  renderRecentSearches() {
    if (!this.searchHistory || !this.searchHistoryList) return;

    const recentSearches = this.getRecentSearches();
    this.searchHistory.classList.toggle('hidden', recentSearches.length === 0);
    this.searchHistoryList.innerHTML = recentSearches
      .map((search) => {
        const escapedSearch = this.escapeHTML(search);
        const searchUrl = `${this.searchUrl}?q=${encodeURIComponent(search)}`;

        return `
          <li class="predictive-search__list-item search-history__list-item" role="option" aria-selected="false">
            <a href="${searchUrl}" class="predictive-search__item search-history__item link link--text" tabindex="-1" data-search-history-term="${escapedSearch}">
              <span class="search-history__icon" aria-hidden="true">
                <svg class="icon icon-clock" viewBox="0 0 24 24" focusable="false">
                  <circle cx="12" cy="12" r="9"></circle>
                  <path d="M12 7v5l3 2"></path>
                </svg>
              </span>
              <span class="predictive-search__item-heading search-history__term h5">${escapedSearch}</span>
            </a>
          </li>
        `;
      })
      .join('');
  }

  markRecentPredictiveSuggestions() {
    const recentSearches = this.getRecentSearches().map((search) => search.toLowerCase());

    this.querySelectorAll('#predictive-search-results-queries-list .predictive-search__item').forEach((item) => {
      const searchTerm = this.normalizeSearchTerm(this.getSearchTermFromItem(item));
      const recentLabel = item.querySelector('[data-recent-search-label]');
      const shouldShowRecentLabel = searchTerm && recentSearches.includes(searchTerm.toLowerCase());

      if (!shouldShowRecentLabel) {
        recentLabel?.remove();
        return;
      }

      if (!recentLabel) {
        item
          .querySelector('.predictive-search__item-heading')
          ?.insertAdjacentHTML('beforeend', '<span class="predictive-search__recent-label" data-recent-search-label>Recent</span>');
      }
    });
  }

  getSearchTermFromItem(item) {
    const explicitSearchTerm = item.dataset.searchHistoryTerm;
    if (explicitSearchTerm) return explicitSearchTerm;

    const labelledSearchTerm = item.querySelector('[aria-label]')?.getAttribute('aria-label');
    if (labelledSearchTerm) return labelledSearchTerm;

    if (item.closest('[data-search-history-list], .search-suggestions__results-list, #predictive-search-results-queries-list')) {
      const heading = item.querySelector('.predictive-search__item-heading');
      if (!heading) return item.textContent;

      const headingClone = heading.cloneNode(true);
      headingClone.querySelector('[data-recent-search-label]')?.remove();
      return headingClone.textContent;
    }

    return this.getQuery();
  }

  normalizeSearchTerm(searchTerm) {
    return searchTerm?.replace(/\s+/g, ' ').trim();
  }

  escapeHTML(value) {
    return value.replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return entities[character];
    });
  }
}

customElements.define('search-form', SearchForm);
